// ============================================================================
// frontend/src/pages/EditTenant.tsx
// ============================================================================

import { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Page from 'pages/Page';
import { useAuth, ChainlitContext } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// Composants réutilisés
import { BrandingSection } from '@/components/tenant/BrandingSection';
import { ThemeSelector } from '@/components/tenant/ThemeSelector';
import { ThemeCustomizer } from '@/components/tenant/ThemeCustomizer';
import { ThemePreview } from '@/components/tenant/ThemePreview';

// Hook pour le thème
import { useTenantForm } from '@/hooks/useTenantForm';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  database_name: string;
  admin_email: string;
  app_name: string;
  status: string;
  deployment_status: string;
  metadata: {
    tenant_name?: string;
    logo_light?: File | null;
    logo_dark?: File | null;
    favicon?: File | null;
    primary_color?: string;
    accent_color?: string;
    secondary_color?: string;
    destructive_color?: string;
    dark_primary_color?: string;
    dark_accent_color?: string;
    dark_background_color?: string;
    dark_foreground_color?: string;
    dark_card_color?: string;
    font_family?: string;
    border_radius?: string;
    [key: string]: any;
  };
}

const EditTenantContent = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Utiliser le hook de thème
  const {
    formData,
    setFormData,
    selectedTheme,
    customTheme,
    setCustomTheme,
    handleFileChange,
    applyTheme
  } = useTenantForm();

  // Form state de base (non lié au thème)
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    app_name: '',
    admin_email: '',
    status: 'active'
  });

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchTenant();
  }, [isAuthenticated, isReady, tenantId, navigate]);

  const fetchTenant = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/tenants/${tenantId}`)
      );

      if (!response.ok) {
        throw new Error('Erreur lors du chargement du tenant');
      }

      const data = await response.json();
      setTenant(data);

      // Pré-remplir les infos de base
      setBasicInfo({
        name: data.name || '',
        app_name: data.app_name || '',
        admin_email: data.admin_email || '',
        status: data.status || 'active'
      });

      // Pré-remplir les données de thème dans formData
      const metadata = data.metadata || {};
      setFormData({
        ...formData,
        // Branding
        logo_light: null,
        logo_dark: null,
        favicon: null,
        login_page_image: null,
        
        // Couleurs (avec fallback sur les valeurs par défaut)
        primary_color: metadata.primary_color || '#2563eb',
        accent_color: metadata.accent_color || '#3b82f6',
        secondary_color: metadata.secondary_color || '#f3f4f6',
        destructive_color: metadata.destructive_color || '#ef4444',
        
        // Mode sombre
        dark_primary_color: metadata.dark_primary_color || '#3b82f6',
        dark_accent_color: metadata.dark_accent_color || '#60a5fa',
        dark_background_color: metadata.dark_background_color || '#0f172a',
        dark_foreground_color: metadata.dark_foreground_color || '#f1f5f9',
        dark_card_color: metadata.dark_card_color || '#1e293b',
        
        // Typographie
        font_family: metadata.font_family || 'inter',
        border_radius: metadata.border_radius || '0.5'
      });

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      // Préparer les métadonnées de thème à envoyer
      const themeMetadata = {
        tenant_name: basicInfo.name,
        primary_color: formData.primary_color,
        accent_color: formData.accent_color,
        secondary_color: formData.secondary_color,
        destructive_color: formData.destructive_color,
        dark_primary_color: formData.dark_primary_color,
        dark_accent_color: formData.dark_accent_color,
        dark_background_color: formData.dark_background_color,
        dark_foreground_color: formData.dark_foreground_color,
        dark_card_color: formData.dark_card_color,
        font_family: formData.font_family,
        border_radius: formData.border_radius
      };

      const response = await fetch(
        apiClient.buildEndpoint(`/api/tenants/${tenantId}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: basicInfo.name,
            app_name: basicInfo.app_name,
            admin_email: basicInfo.admin_email,
            status: basicInfo.status,
            metadata: themeMetadata
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de la mise à jour');
      }

      setSuccessMessage('✅ Tenant mis à jour avec succès');
      
      // Rafraîchir les données
      await fetchTenant();
      
      // Rediriger après 2 secondes
      setTimeout(() => {
        navigate(`/admin/tenants/${tenantId}`);
      }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isReady || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-8" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  if (error && !tenant) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <svg className="w-16 h-16 text-destructive mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-lg font-medium mb-2">{error}</p>
        <button
          onClick={() => navigate('/admin/tenants')}
          className="mt-4 px-4 py-2 rounded-md border border-input hover:bg-accent"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <div className="max-w-4xl mx-auto w-full p-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/admin/tenants/${tenantId}`)}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour aux détails
          </button>
          <h1 className="text-3xl font-bold mb-2">✏️ Modifier le tenant</h1>
          <p className="text-muted-foreground">
            Modifiez les informations et l'apparence du tenant <strong>{tenant?.name}</strong>
          </p>
        </div>

        {/* Info Box - Limitations */}
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                ℹ️ Informations importantes
              </p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                <li>Les modifications de branding et de thème sont appliquées immédiatement</li>
                <li>Le sous-domaine et la base de données ne peuvent pas être modifiés</li>
                <li>Pour modifier OAuth, ElevenLabs ou les agents, contactez un administrateur</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 rounded-md border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800 p-4 text-sm text-green-700 dark:text-green-300 animate-slideIn">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive animate-shake">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Informations de base */}
          <div className="rounded-lg border bg-card p-6 space-y-4">
            <h2 className="text-xl font-semibold border-b pb-3">📋 Informations de base</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Nom du cabinet <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={basicInfo.name}
                  onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-md border border-input bg-background"
                  placeholder="Cabinet Martin & Associés"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Nom de l'application <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={basicInfo.app_name}
                  onChange={(e) => setBasicInfo({ ...basicInfo, app_name: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-md border border-input bg-background"
                  placeholder="SmartLawyer Cabinet Martin"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Email administrateur <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  value={basicInfo.admin_email}
                  onChange={(e) => setBasicInfo({ ...basicInfo, admin_email: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-md border border-input bg-background"
                  placeholder="admin@cabinet-martin.fr"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  ⚠️ Ceci ne change pas les identifiants de connexion existants
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Statut</label>
                <select
                  value={basicInfo.status}
                  onChange={(e) => setBasicInfo({ ...basicInfo, status: e.target.value })}
                  className="w-full px-4 py-2 rounded-md border border-input bg-background"
                >
                  <option value="active">✅ Actif</option>
                  <option value="inactive">❌ Inactif</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Informations non modifiables */}
          <div className="rounded-lg border bg-muted/30 p-6 space-y-4">
            <h2 className="text-xl font-semibold border-b pb-3">🔒 Informations techniques (lecture seule)</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Sous-domaine
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={tenant?.subdomain || ''}
                    disabled
                    className="flex-1 px-4 py-2 rounded-md border border-input bg-muted text-muted-foreground cursor-not-allowed"
                  />
                  <span className="text-sm text-muted-foreground">.smartlawyer.ai</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Base de données
                </label>
                <input
                  type="text"
                  value={tenant?.database_name || ''}
                  disabled
                  className="w-full px-4 py-2 rounded-md border border-input bg-muted text-muted-foreground cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Statut déploiement
                </label>
                <input
                  type="text"
                  value={tenant?.deployment_status || ''}
                  disabled
                  className="w-full px-4 py-2 rounded-md border border-input bg-muted text-muted-foreground cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  URL tenant
                </label>
                <input
                  type="text"
                  value={`https://${tenant?.subdomain}.smartlawyer.ai`}
                  disabled
                  className="w-full px-4 py-2 rounded-md border border-input bg-muted text-muted-foreground cursor-not-allowed"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              ℹ️ Ces informations ne peuvent pas être modifiées après la création du tenant
            </p>
          </div>

          {/* Section 3: Branding - Réutilisation du composant */}
          <BrandingSection formData={formData} handleFileChange={handleFileChange} />

          {/* Section 4: Thème - Réutilisation des composants */}
          <div className="rounded-lg border bg-card p-6 space-y-6">
            <h2 className="text-xl font-semibold border-b pb-3">🎨 Thème & Apparence</h2>
            
            <ThemeSelector
              selectedTheme={selectedTheme}
              customTheme={customTheme}
              onThemeSelect={applyTheme}
              onCustomThemeToggle={(enabled) => {
                setCustomTheme(enabled);
                if (!enabled) {
                  applyTheme(selectedTheme);
                }
              }}
            />

            {customTheme && (
              <ThemeCustomizer formData={formData} setFormData={setFormData} />
            )}
          </div>

          {/* Section 5: Preview - Réutilisation du composant */}
          <ThemePreview
            formData={formData}
            selectedTheme={selectedTheme}
            customTheme={customTheme}
          />

          {/* Section 6: Configurations avancées (lecture seule) */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800 p-6">
            <div className="flex items-start gap-3 mb-4">
              <svg className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-orange-900 dark:text-orange-100 mb-2">
                  ⚙️ Configurations avancées (non modifiable ici)
                </h2>
                <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
                  Les éléments suivants nécessitent un redéploiement Helm et ne peuvent pas être modifiés via cette interface :
                </p>
                <ul className="text-sm text-orange-700 dark:text-orange-300 space-y-1 list-disc list-inside ml-3">
                  <li><strong>OAuth Providers</strong> : Configuration Google OAuth, Azure AD</li>
                  <li><strong>ElevenLabs</strong> : API key et Voice ID</li>
                  <li><strong>Agents disponibles</strong> : Liste des agents juridiques activés</li>
                  <li><strong>Variables d'environnement</strong> : Configuration système</li>
                </ul>
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-3">
                  💡 Pour modifier ces paramètres, contactez un administrateur système
                </p>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex flex-1 items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <>
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Enregistrement...
                </>
              ) : (
                '💾 Enregistrer les modifications'
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/admin/tenants/${tenantId}`)}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background px-6 py-3 text-sm font-medium hover:bg-accent disabled:opacity-50 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default function EditTenant() {
  return (
    <Page>
      <EditTenantContent />
    </Page>
  );
}